import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useCompany } from "@/lib/company";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Save, Percent, Upload, FileText } from "lucide-react";
/**
 * Tax Library — dedicated CRUD page under Accounting.
 *
 * Pros can rename or delete rates here without opening an invoice/bill.
 * Deletion is refused when the rate is still applied to any active
 * document (backend enforces).
 */
export default function TaxLibrary({ embedded = false } = {}) {
  const { currentId } = useCompany();
  const [taxes, setTaxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // { id, name, rate }
  const [importing, setImporting] = useState(false);

  const load = async () => {
    if (!currentId) return;
    setLoading(true);
    try {
      const r = await api.get(`/companies/${currentId}/taxes`);
      setTaxes(r.data.taxes || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load taxes");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [currentId]);

  const remove = async (t) => {
    if (!window.confirm(`Delete tax "${t.name}"? This can't be undone.`)) return;
    try {
      await api.delete(`/companies/${currentId}/taxes/${t.id}`);
      toast.success(`Deleted "${t.name}"`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className={embedded ? "space-y-4" : "max-w-4xl mx-auto space-y-4"}>
      {!embedded && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight">Tax Library</h1>
            <p className="text-sm text-slate-500 mt-1">
              Manage the tax rates used on invoices and bills. Rename or delete rates without opening a document.
            </p>
          </div>
        </div>
      )}
      <div className="flex items-center gap-2 justify-end">
        <button
          data-testid="tax-library-import"
          onClick={() => setImporting(true)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-slate-200 bg-white text-slate-700 text-sm hover:bg-slate-50"
        ><Upload size={14} /> Import CSV</button>
        <button
          data-testid="tax-library-new"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-sm shadow-sm"
        ><Plus size={14} /> New tax</button>
      </div>

      <div className="rounded-lg border bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading taxes…</div>
        ) : taxes.length === 0 ? (
          <div className="p-12 text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center mb-3">
              <Percent className="text-indigo-500" size={20} />
            </div>
            <h3 className="font-heading font-semibold text-slate-800">No taxes yet</h3>
            <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
              Add rates like GST, HST, VAT, or your state sales tax so you can apply them per-line on invoices and bills.
            </p>
            <button
              onClick={() => setCreating(true)}
              className="mt-4 inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-sm"
              data-testid="tax-library-empty-new"
            ><Plus size={14} /> Add your first tax</button>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-right px-4 py-2">Rate</th>
                <th className="text-left px-4 py-2">Linked payable</th>
                <th className="w-32"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {taxes.map((t) => (
                <tr key={t.id} data-testid={`tax-row-${t.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-800">{t.name}</td>
                  <td className="px-4 py-3 text-right font-mono-num">{Number(t.rate).toFixed(2)}%</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">
                    {t.payable_account_id
                      ? <PayableName currentId={currentId} accountId={t.payable_account_id} />
                      : <span className="text-slate-400 italic">Default</span>}
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button
                      onClick={() => setEditing({ id: t.id, name: t.name, rate: String(t.rate),
                                                    payable_account_id: t.payable_account_id })}
                      className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
                      title="Edit"
                      data-testid={`tax-edit-${t.id}`}
                    ><Pencil size={13} /></button>
                    <button
                      onClick={() => remove(t)}
                      className="p-1.5 rounded hover:bg-red-50 text-red-500"
                      title="Delete"
                      data-testid={`tax-delete-${t.id}`}
                    ><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {creating && (
        <TaxDialog
          currentId={currentId}
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); load(); }}
        />
      )}
      {editing && (
        <TaxDialog
          currentId={currentId}
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {importing && (
        <BulkImportDialog
          currentId={currentId}
          onClose={() => setImporting(false)}
          onImported={() => { setImporting(false); load(); }}
        />
      )}
    </div>
  );
}

function PayableName({ currentId, accountId }) {
  // Trivially memoised at module scope so re-renders don't hammer the API.
  const [name, setName] = useState(_PAYABLE_NAME_CACHE.get(accountId) || "");
  useEffect(() => {
    if (!currentId || !accountId) return;
    if (_PAYABLE_NAME_CACHE.has(accountId)) {
      setName(_PAYABLE_NAME_CACHE.get(accountId));
      return;
    }
    api.get(`/companies/${currentId}/accounts`).then(r => {
      const hit = (r.data.accounts || []).find(a => a.id === accountId);
      const n = hit?.name || "—";
      _PAYABLE_NAME_CACHE.set(accountId, n);
      setName(n);
    }).catch(() => setName("—"));
  }, [currentId, accountId]);
  return <span className="text-slate-600">{name || "…"}</span>;
}
const _PAYABLE_NAME_CACHE = new Map();


function TaxDialog({ currentId, initial, onClose, onSaved }) {
  const [name, setName] = useState(initial?.name || "");
  const [rate, setRate] = useState(initial?.rate || "");
  const [payableAccountId, setPayableAccountId] = useState(initial?.payable_account_id || "");
  const [liabilityAccts, setLiabilityAccts] = useState([]);
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial;

  // Load liability CoA once for the "Linked payable" picker.
  useEffect(() => {
    if (!currentId) return;
    api.get(`/companies/${currentId}/accounts`)
      .then(r => setLiabilityAccts((r.data.accounts || [])
        .filter(a => a.type === "liability")))
      .catch(() => {});
  }, [currentId]);

  const submit = async () => {
    const clean = name.trim();
    const r = parseFloat(rate);
    if (!clean) { toast.error("Tax name is required"); return; }
    if (isNaN(r) || r < 0 || r > 100) { toast.error("Rate must be between 0 and 100"); return; }
    setSaving(true);
    try {
      const body = { name: clean, rate: r, payable_account_id: payableAccountId || null };
      if (isEdit) {
        await api.patch(`/companies/${currentId}/taxes/${initial.id}`, body);
        toast.success(`Updated "${clean}"`);
      } else {
        await api.post(`/companies/${currentId}/taxes`, body);
        toast.success(`Tax "${clean}" created`);
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5 space-y-4" data-testid="tax-dialog">
        <div className="flex items-center justify-between border-b pb-3">
          <h3 className="font-heading font-semibold text-lg">{isEdit ? "Edit tax" : "Create a new tax"}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-slate-700 mb-1">Tax name <span className="text-red-500">*</span></label>
            <input
              autoFocus value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. GST"
              className="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 outline-none"
              data-testid="tax-dialog-name"
            />
          </div>
          <div>
            <label className="block text-sm text-slate-700 mb-1">Tax rate <span className="text-red-500">*</span></label>
            <div className="relative">
              <input
                type="number" step="0.01" min="0" max="100" value={rate}
                onChange={(e) => setRate(e.target.value)}
                placeholder="0.00"
                className="w-full border rounded px-3 py-2 text-sm pr-8 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 outline-none"
                data-testid="tax-dialog-rate"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">%</span>
            </div>
          </div>
          <div>
            <label className="block text-sm text-slate-700 mb-1">Linked payable account</label>
            <select
              value={payableAccountId}
              onChange={(e) => setPayableAccountId(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 outline-none"
              data-testid="tax-dialog-payable"
            >
              <option value="">Default (Sales Tax Payable — auto)</option>
              {liabilityAccts.map(a => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-slate-400 mt-1">
              Where collected tax accrues on the balance sheet. Leave as
              default unless you file with a specific agency payable.
            </p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pt-3 border-t">
          <button onClick={onClose} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">Cancel</button>
          <button
            onClick={submit}
            disabled={saving || !name.trim() || rate === ""}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-sm disabled:opacity-50"
            data-testid="tax-dialog-submit"
          ><Save size={13} /> {saving ? "Saving…" : (isEdit ? "Save changes" : "Create tax")}</button>
        </div>
      </div>
    </div>
  );
}

/**
 * BulkImportDialog — accepts pasted CSV text and previews rows before
 * hitting the backend. Supports header row with columns `name` and
 * `rate` (case-insensitive) OR headerless "Name,Rate" pairs.
 */
function BulkImportDialog({ currentId, onClose, onImported }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const preview = parseCsv(text);
  const validCount = preview.filter(r => r.valid).length;
  const invalidCount = preview.length - validCount;

  const submit = async () => {
    if (validCount === 0) { toast.error("No valid rows to import"); return; }
    setSaving(true);
    try {
      const rows = preview.filter(r => r.valid).map(r => ({ name: r.name, rate: r.rate }));
      const resp = await api.post(`/companies/${currentId}/taxes/bulk-import`, { rows });
      const { created, updated, skipped } = resp.data;
      const parts = [];
      if (created) parts.push(`${created} created`);
      if (updated) parts.push(`${updated} updated`);
      if (skipped?.length) parts.push(`${skipped.length} skipped`);
      toast.success(parts.join(" · ") || "Done");
      onImported();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl p-5 space-y-4" data-testid="tax-import-dialog">
        <div className="flex items-center justify-between border-b pb-3">
          <h3 className="font-heading font-semibold text-lg inline-flex items-center gap-2">
            <FileText size={18} /> Import taxes from CSV
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
        </div>

        <div className="space-y-2">
          <div className="text-sm text-slate-600">
            Paste CSV rows below — one tax per line. Supports optional header row.
            <br />
            Existing tax names are updated with the new rate (idempotent).
          </div>
          <details className="text-xs bg-slate-50 border rounded p-2">
            <summary className="cursor-pointer text-slate-600 font-medium">Example</summary>
            <pre className="mt-2 text-[11px] text-slate-500">name,rate{"\n"}GST,5{"\n"}HST,13{"\n"}QST,9.975{"\n"}VAT,20</pre>
          </details>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={9}
            placeholder={`name,rate\nGST,5\nHST,13`}
            className="w-full border rounded px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 outline-none"
            data-testid="tax-import-textarea"
          />
        </div>

        {preview.length > 0 && (
          <div className="border rounded max-h-56 overflow-y-auto">
            <table className="w-full text-xs" data-testid="tax-import-preview">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-1.5 w-8">#</th>
                  <th className="text-left px-3 py-1.5">Name</th>
                  <th className="text-right px-3 py-1.5 w-20">Rate</th>
                  <th className="px-3 py-1.5 w-24">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {preview.map((r, i) => (
                  <tr key={i} className={r.valid ? "" : "bg-red-50/30"}>
                    <td className="px-3 py-1 text-slate-400">{i + 1}</td>
                    <td className="px-3 py-1">{r.name || <span className="text-slate-400 italic">(empty)</span>}</td>
                    <td className="px-3 py-1 text-right font-mono-num">{r.rate ?? ""}%</td>
                    <td className="px-3 py-1">
                      {r.valid
                        ? <span className="text-emerald-600">OK</span>
                        : <span className="text-red-600" title={r.error}>Skip · {r.error}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between pt-3 border-t">
          <div className="text-xs text-slate-500">
            {preview.length > 0 && (
              <>
                <span className="text-emerald-600 font-medium">{validCount} valid</span>
                {invalidCount > 0 && <span className="text-red-600 ml-2">{invalidCount} skipped</span>}
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">Cancel</button>
            <button
              onClick={submit}
              disabled={saving || validCount === 0}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-sm disabled:opacity-50"
              data-testid="tax-import-submit"
            ><Upload size={13} /> {saving ? "Importing…" : `Import ${validCount || ""} row${validCount === 1 ? "" : "s"}`}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Very small CSV parser tuned for the two-column {name, rate} case.
 * Supports quoted names (for commas in tax labels like "PST, Manitoba").
 * Header row detected when the first row's first cell is literally "name"
 * (case-insensitive) — otherwise every row is treated as data.
 */
function parseCsv(text) {
  if (!text || !text.trim()) return [];
  const lines = text.replace(/\r/g, "").split("\n").map(l => l.trim()).filter(Boolean);
  const splitRow = (line) => {
    // Handle quoted commas.
    const cells = []; let cur = ""; let quoted = false;
    for (const ch of line) {
      if (ch === '"') { quoted = !quoted; continue; }
      if (ch === "," && !quoted) { cells.push(cur.trim()); cur = ""; continue; }
      cur += ch;
    }
    cells.push(cur.trim());
    return cells;
  };
  const hasHeader = /^\s*name\s*$/i.test(splitRow(lines[0])[0] || "");
  const dataLines = hasHeader ? lines.slice(1) : lines;
  return dataLines.map((line) => {
    const cells = splitRow(line);
    const name = cells[0] || "";
    const rawRate = (cells[1] || "").replace(/%/g, "").trim();
    const rate = rawRate === "" ? NaN : Number(rawRate);
    let valid = true;
    let error = "";
    if (!name) { valid = false; error = "name is empty"; }
    else if (!Number.isFinite(rate)) { valid = false; error = "rate is not a number"; }
    else if (rate < 0 || rate > 100) { valid = false; error = "rate must be between 0 and 100"; }
    return { name, rate: Number.isFinite(rate) ? rate : null, valid, error };
  });
}

